import React, { useState, useEffect, useRef } from "react";
import { ExternalLink, Copy, Inbox } from "lucide-react";
import CryptoSelector from "../../components/CryptoSelector";
import BalancesSection from "../../components/BalancesSection";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../utils/apiConfig";
import Balance from "../NewDashboard/Balance";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchRecentTransactions as fetchRecentTransactionsAction,
    selectTransactions,
    selectTransactionsLoading,
    selectTransactionsError,
    setActiveTab as setActiveTabAction,
} from "../../store/slices/transactionsSlice";
import { useTranslation } from "react-i18next";

const RecentDashboard = ({ dashboardStats }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // Redux state - transactions
    const recentTransactions = useSelector(selectTransactions);
    const loading = useSelector(selectTransactionsLoading);
    const error = useSelector(selectTransactionsError);
    const reduxActiveTab = useSelector((state) => state.transactions.activeTab);

    // Local state
    const [activeTab, setActiveTab] = useState("Deposit");
    const [displayedCount, setDisplayedCount] = useState(4);

    const handleNavigate = () => {
        navigate("/New-Dashboard/transactions");
    };

    // Copy text to clipboard
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "Paid":
            case "completed":
            case "confirmed":
                return "bg-green-100 text-green-800";
            case "pending":
            case "processing":
                return "bg-yellow-100 text-yellow-800";
            case "failed":
            case "rejected":
            case "cancelled":
                return "bg-red-100 text-red-800";
            case "expired":
                return "bg-gray-100 text-gray-800";
            default:
                return "bg-blue-100 text-blue-800";
        }
    };

    const StatCard = ({
        title,
        value,
        subtitle,
        showDropdown = false,
        showView = false,
    }) => (
        <div className="bg-white rounded-[25px] p-4">
            <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                    <h3 className="text-lg font-lg text-gray-600">{title}</h3>
                </div>
                {showView && (
                    <button className="text-[#348BED] text-sm font-medium hover:text-blue-800"
                        onClick={handleNavigate}
                    >
                        {t("common.view")}
                    </button>
                )}
            </div>
            <div className="text-2xl font-medium text-gray-900 pt-8">{value}</div>
            <div className="text-sm text-gray-500">{subtitle}</div>
        </div>
    );

    const truncateAddress = (address, start = 6, end = 4) => {
        if (!address) return '';
        if (address.length <= start + end) return address;
        return `${address.substring(0, start)}x...${address.substring(address.length - end)}`;
    };

    // Track previous activeTab to detect actual changes
    const prevActiveTabRef = useRef(activeTab);
    const initialMountRef = useRef(true);

    // Update Redux activeTab when local activeTab changes
    useEffect(() => {
        if (activeTab !== reduxActiveTab) {
            dispatch(setActiveTabAction(activeTab));
        }
    }, [activeTab, reduxActiveTab, dispatch]);

    // Fetch data only when activeTab actually changes (not on scroll or re-renders)
    useEffect(() => {
        const tabChanged = prevActiveTabRef.current !== activeTab;
        const isInitialMount = initialMountRef.current;

        // Only fetch if the tab actually changed or on initial mount
        if (tabChanged || isInitialMount) {
            dispatch(fetchRecentTransactionsAction({
                activeTab,
                forceRefresh: tabChanged
            }));
            prevActiveTabRef.current = activeTab;
            initialMountRef.current = false;
        }
    }, [activeTab, dispatch]);

    const tabs = [
        { key: "Deposit", label: t("recentDashboard.deposit") },
        { key: "Payouts", label: t("recentDashboard.payouts") },
        { key: "Withdrawal", label: t("recentDashboard.withdrawal") },
    ];

    return (
        <div className="bg-gray-100">
            <div className="flex flex-col lg:flex-row gap-4 py-3">
                {/* Recent Activity Section */}
                <div className="bg-white rounded-[25px] shadow-sm p-4 lg:p-6 flex-1 lg:flex-[1.3]">
                    <h2 className="text-lg font-semibold text-gray-800 mb-3">
                        {t("recentDashboard.recentActivity")}
                    </h2>

                    {/* Tabs */}
                    <div className="flex flex-wrap gap-4 lg:space-x-6 mb-4 border-b border-gray-200">
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`pb-1 text-sm font-medium transition-colors ${activeTab === tab.key
                                    ? "text-blue-600 border-b-2 border-blue-600"
                                    : "text-gray-500 hover:text-gray-700"
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Skeleton Loader */}
                    {loading && (
                        <div className="overflow-x-auto -mx-4 lg:mx-0">
                            <table className="w-full min-w-[600px]">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="text-left py-2 px-4 text-xs lg:text-sm font-medium text-gray-600">
                                            {t("recentDashboard.amount")}
                                        </th>
                                        <th className="text-left py-2 px-4 text-xs lg:text-sm font-medium text-gray-600">
                                            {t("recentDashboard.destinations")}
                                        </th>
                                        <th className="text-left py-2 px-4 text-xs lg:text-sm font-medium text-gray-600">
                                            {t("recentDashboard.date")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...Array(4)].map((_, index) => (
                                        <tr key={`skeleton-${index}`} className="border-b border-gray-100">
                                            <td className="py-2 px-4">
                                                <span className="text-xs lg:text-sm inline-block h-4 bg-gray-200 rounded animate-pulse w-24"></span>
                                                <span className="inline-flex items-center h-6 bg-gray-200 rounded-[5px] animate-pulse w-16 ml-2"></span>
                                            </td>
                                            <td className="py-2 px-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs lg:text-sm inline-block h-4 bg-gray-200 rounded animate-pulse w-28 font-mono"></span>
                                                    <span className="inline-block h-3 w-3 bg-gray-200 rounded animate-pulse"></span>
                                                </div>
                                            </td>
                                            <td className="py-2 px-4">
                                                <span className="text-xs lg:text-sm inline-block h-4 bg-gray-200 rounded animate-pulse w-24"></span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center">
                                <svg
                                    className="w-4 h-4 text-red-400 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                <span className="text-red-700 text-sm">{error}</span>
                            </div>
                        </div>
                    )}

                    {/* Table */}
                    {!loading && !error && recentTransactions.length > 0 && (
                        <div className="overflow-x-auto -mx-4 lg:mx-0">
                            <table className="w-full min-w-[600px]">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="text-left py-2 px-4 text-xs lg:text-sm font-medium text-gray-600">
                                            {t("recentDashboard.amount")}
                                        </th>
                                        <th className="text-left py-2 px-4 text-xs lg:text-sm font-medium text-gray-600">
                                            {t("recentDashboard.destinations")}
                                        </th>
                                        {/* <th className="text-left py-2 px-4 text-xs lg:text-sm font-medium text-gray-600">
                                            Status
                                        </th> */}
                                        <th className="text-left py-2 px-4 text-xs lg:text-sm font-medium text-gray-600">
                                            {t("recentDashboard.date")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentTransactions?.slice(0, displayedCount)?.map((item) => (
                                        <tr key={item.id} className="border-b border-gray-100">

                                            <td className="py-2 px-4">
                                                <span className="text-xs lg:text-sm font-semibold text-gray-800">
                                                    {item.amount}

                                                </span>
                                                <span
                                                    className={`inline-flex items-center px-2 py-1 rounded-[5px] text-xs font-medium ml-2 ${getStatusColor(
                                                        item.status
                                                    )}`}
                                                >
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td className="py-2 px-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs lg:text-sm text-gray-600 font-mono">
                                                        {truncateAddress(item.destination ?? "--")}
                                                        {/* {item.destination && item.invoiceNo !== "N/A"
                                                            ? item.destination.length > 16
                                                                ? item.destination.slice(0, 16) + "..."
                                                                : item.destination
                                                            : "N/A"} */}
                                                    </span>

                                                    {item.destination && item.destination !== "N/A" && (
                                                        <button
                                                            onClick={() => copyToClipboard(item.destination)}
                                                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                                                            title={t("recentDashboard.copyDestination")}
                                                        >
                                                            <Copy className="w-3 h-3 text-gray-500" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            {/* <td className="py-2 px-4">
                                                <span
                                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                                        item.status
                                                    )}`}
                                                >
                                                    {item.status}
                                                </span>
                                            </td> */}
                                            <td className="py-2 px-4">
                                                <span className="text-xs lg:text-sm text-gray-600">
                                                    {item.date}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* No Data Found */}
                    {!loading && !error && recentTransactions.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-12 px-4">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <Inbox className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">{t("recentDashboard.noDataFound")}</h3>
                            <p className="text-sm text-gray-500 text-center max-w-sm">
                                {t("recentDashboard.noTransactionsMessage", { 
                                    type: activeTab === "Deposit" 
                                        ? t("recentDashboard.deposit").toLowerCase()
                                        : activeTab === "Payouts"
                                        ? t("recentDashboard.payouts").toLowerCase()
                                        : t("recentDashboard.withdrawal").toLowerCase()
                                })}
                            </p>
                        </div>
                    )}

                    {/* View More */}

                    <div className="mt-3 pt-3 border-t border-gray-200">
                        <button
                            className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                            onClick={handleNavigate}
                        >
                            <span>{t("recentDashboard.viewMore")}</span>
                            <ExternalLink className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Reports Section */}
                <div className="grid grid-rows-2 gap-3 lg:gap-4 flex-1 lg:flex-[1]">
                    <div className="bg-white rounded-[15px] shadow-sm p-3 lg:p-4">
                        <StatCard
                            title={t("recentDashboard.averageDepositPayment")}
                            value={`$${dashboardStats?.averageDepositPayment || 0}`}
                            subtitle={`${t("dashboard.deposited")} ${new Date().toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                            })}`}
                            showView={true}
                        />
                    </div>

                    <div className="bg-white rounded-[15px] shadow-sm p-3 lg:p-4">
                        <StatCard
                            title={t("recentDashboard.averagePayout")}
                            value={`$${dashboardStats?.averagePayout || 0}`}
                            subtitle={`${t("dashboard.deposited")} ${new Date().toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                            })}`}
                            showView={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecentDashboard;
